import axios from 'axios';
import { ADVISORY_PARTNER_MEMBER_ROLES } from '@/constants';
import { API_ROOT, logApiError } from '@/store/api';
import { FLAG_ADVISOR_CLIENT_SHARING, isFeatureEnabled } from '@/store/feature_flags';
import { isTenantPartner, mergeWorkspaceProfile } from '@/services/advisoryPartner';
import { trackPosthogEvent } from '@/posthog_tracking';

const ADMIN_ROLE = ADVISORY_PARTNER_MEMBER_ROLES.admin.toLowerCase();

/**
 * Check if advisor can access the specified advisor's clients.
 * Allowlist approach: only admin can access any, member can only access own.
 */
function canAccessAdvisorClients(advisorProfile, targetAdvisorId) {
    // Nothing to read: the profile isn't loaded yet, or the target is the viewer's own
    // book and there is none — a superuser running a workspace they don't belong to
    // has admin reach but no member uuid (`is_acting_admin`). No URL to build either way.
    if (!advisorProfile?.role || !targetAdvisorId) {
        return false;
    }

    // Client sharing (ENG-2789) makes member → colleague access legal (shared
    // books, share-all workspaces); the backend permission is the source of
    // truth there, so stop pre-blocking and let it decide.
    if (isFeatureEnabled(FLAG_ADVISOR_CLIENT_SHARING)) {
        return true;
    }

    const isAdmin = advisorProfile.role === ADMIN_ROLE;
    // Member ids in URLs (and hence targetAdvisorId) are uuids.
    const isOwnClients = advisorProfile.uuid === targetAdvisorId;

    // Admin can access any advisor's clients, member only their own
    return isAdmin || isOwnClients;
}

/** First human-readable message out of a DRF error body (field errors or detail). */
function apiErrorKind(e) {
    const data = e.response?.data;
    if (!data) return null;
    if (typeof data.detail === 'string') return data.detail;
    const first = Object.values(data).find(v => Array.isArray(v) && v.length);
    return first ? String(first[0]) : null;
}

export default {
    namespaced: true,
    state: {
        advisorProfile: {},
        // The workspace the advisor pages act on — the host's, or for an advisor whose
        // firm has no live subdomain their own membership (see syncAdvisoryPartnerProfile).
        advisoryPartnerProfile: {},
        // The host's partner and nothing else: the only source of branding.
        brandProfile: {},
        marketingEvents: {},
    },
    mutations: {
        updateAdvisorProfile(state, advisorProfile) {
            state.advisorProfile = advisorProfile;
        },
        updateAdvisoryPartnerProfile(state, advisoryPartnerProfile) {
            state.advisoryPartnerProfile = advisoryPartnerProfile;
        },
        updateBrandProfile(state, brandProfile) {
            state.brandProfile = brandProfile;
        },
        updateMarketingEvents(state, marketingEvents) {
            state.marketingEvents = marketingEvents;
        },
    },
    getters: {
        advisor_profile: state => {
            return state.advisorProfile || {};
        },
        advisory_partner_profile: state => {
            return state.advisoryPartnerProfile || {};
        },
        brand_profile: state => {
            return state.brandProfile || {};
        },
        // A superuser running a workspace they don't belong to (the backend's
        // acts_as_workspace_admin): an admin's reach, but no membership row there — so
        // no book of their own, nothing of theirs to share, nowhere to file a new client.
        is_acting_workspace_admin: state => {
            return Boolean(state.advisorProfile?.is_acting_admin);
        },
        marketing_events: state => {
            return state.marketingEvents || {};
        },
    },
    actions: {
        async assignScenario({ dispatch, rootGetters }, params) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/assign_scenario/${params.scenario_id}/${params.user_id}/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },

        // Create a client record WITHOUT inviting them — no account, no email. Used by the
        // scenario save/assign flow so an advisor can plan for a prospect who isn't on
        // Valur. Deliberately NOT the `register` action: that one always mails an
        // activation link, and its analytics event carries the client's email.
        async createUninvitedClient({ dispatch, rootGetters }, params) {
            try {
                const { data } = await axios.post(`${API_ROOT}/clients/uninvited/`, params, rootGetters.axiosConfig);
                trackPosthogEvent('Added Uninvited Client', {});
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },

        // Promote an uninvited client to a real account and send the activation email.
        async inviteClient({ dispatch, rootGetters }, params) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/clients/${params.user_id}/invite/`,
                    { email: params.email || null },
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },

        async getAdvisorInfo({ dispatch }, params) {
            try {
                const { data } = await axios.get(`${API_ROOT}/advisor_info/${params.id}/${params.token}/`);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },

        // Get advisor profile based on current Advisory Partner and user instance.
        // 403 here means the user is not a member of the requested advisory partner;
        // the user.site_domain guard in router.beforeEach is now the single source of
        // truth for partner-domain routing, so no cross-domain redirect happens here.
        async getAdvisorProfile({ dispatch, rootGetters }, advisoryPartnerId) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/advisory/${advisoryPartnerId}/members/current/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                const response = e.response;
                if (!response || response.status !== 403) {
                    logApiError(e, dispatch);
                }
                return false;
            }
        },
        async createOrUpdateAdvisorProfile({ dispatch, rootGetters }, payload = {}) {
            const id = payload.advisor_id;
            let method = 'post';
            let url = `${API_ROOT}/advisory/${payload.advisory_partner_id}/members/`;
            if (id) {
                method = 'patch';
                url = `${url}${id}/`;
            }
            try {
                const { data } = await axios[method](url, payload, rootGetters.axiosConfig);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async listAdvisorClients({ dispatch, rootGetters, state }, payload) {
            // Defensive check: only allow if admin or accessing own clients
            if (!canAccessAdvisorClients(state.advisorProfile, payload.advisor_id)) {
                console.warn('Unauthorized attempt to access advisor clients');
                return [];
            }

            try {
                const { data } = await axios.get(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/members/${payload.advisor_id}/clients/`,
                    { ...rootGetters.axiosConfig, params: payload.params },
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return [];
            }
        },
        async getAdvisorClient({ dispatch, rootGetters, state }, payload) {
            // Defensive check: only allow if admin or accessing own clients
            if (!canAccessAdvisorClients(state.advisorProfile, payload.advisor_id)) {
                console.warn('Unauthorized attempt to access advisor client');
                return null;
            }

            try {
                const ap_id = payload.advisory_partner_id;
                const a_id = payload.advisor_id;
                const c_id = payload.client_id;
                const { data } = await axios.get(
                    `${API_ROOT}/advisory/${ap_id}/members/${a_id}/clients/${c_id}/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return null;
            }
        },

        // Assign specific clients to an advisor (member). Admin-only on the backend;
        // returns the member's updated client list.
        async assignClientsToMember({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/members/${payload.advisor_id}/clients/assign/`,
                    { client_ids: payload.client_ids },
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return null;
            }
        },

        // Remove specific clients from a member's book (admin-only). The clients
        // land in the workspace's Unassigned pool; returns the updated book.
        async unassignClientsFromMember({ dispatch, rootGetters }, payload) {
            const membersRoot = `${API_ROOT}/advisory/${payload.advisory_partner_id}/members`;
            try {
                const { data } = await axios.post(
                    `${membersRoot}/${payload.advisor_id}/clients/unassign/`,
                    { client_ids: payload.client_ids },
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return null;
            }
        },

        // Workspace-wide client list (ENG-2789): the whole firm for admins
        // (including the Unassigned pool — `params: { advisor: 'unassigned' }`),
        // own book ∪ shared books for members. Rows carry advisor_uuid/advisor_name.
        async listPartnerClients({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.get(`${API_ROOT}/advisory/${payload.advisory_partner_id}/clients/`, {
                    ...rootGetters.axiosConfig,
                    params: payload.params,
                });
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return [];
            }
        },

        // Share/unshare one client with the whole workspace (PRD rule 6).
        // Owner-or-admin on the backend; trial workspaces get
        // sharing_unavailable_on_trial back (surfaced via `kind`).
        async shareClientWithWorkspace({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/clients/${payload.client_id}/share/`,
                    { shared: payload.shared },
                    rootGetters.axiosConfig,
                );
                return { ok: true, data };
            } catch (e) {
                const kind = apiErrorKind(e);
                if (e.response?.status !== 400) {
                    logApiError(e, dispatch);
                }
                return { ok: false, kind: kind || 'error' };
            }
        },

        async getClientSharing({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/clients/${payload.client_id}/share/`,
                    rootGetters.axiosConfig,
                );
                return { ok: true, data };
            } catch (e) {
                if (e.response?.status !== 400) logApiError(e, dispatch);
                return { ok: false, kind: apiErrorKind(e) || 'error' };
            }
        },
        async shareClientWithColleague({ dispatch, rootGetters }, payload) {
            // Never let a missing viewer fall through to the workspace-wide API.
            if (!payload.viewer) return { ok: false, kind: 'invalid_share_viewer' };
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/clients/${payload.client_id}/share/`,
                    { shared: payload.shared, viewer: payload.viewer },
                    rootGetters.axiosConfig,
                );
                return { ok: true, data };
            } catch (e) {
                if (e.response?.status !== 400) logApiError(e, dispatch);
                return { ok: false, kind: apiErrorKind(e) || 'error' };
            }
        },

        // Single client through the workspace-wide route — the only detail path
        // that works for Unassigned clients (they have no member scope).
        async getPartnerClient({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/clients/${payload.client_id}/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return null;
            }
        },

        // Get advisory partner profile based on current domain. Returns the profile on
        // success; `null` when the site has no partner (404 — a definitive answer the
        // caller can use to clear stale branding); `undefined` when the request failed
        // inconclusively (network/5xx) so callers can leave the current profile as-is.
        async getAdvisoryPartnerProfile({ dispatch }) {
            try {
                const { data } = await axios.get(`${API_ROOT}/advisory/current/`);
                return data;
            } catch (e) {
                if (e?.response?.status === 404) {
                    return null;
                }
                logApiError(e, dispatch);
                return undefined;
            }
        },
        // Resolve both partner profiles and commit them: the brand and the workspace.
        //
        // Both are the host's answer. A partner subdomain is that firm — its brand and its
        // workspace — and the main app is Valur's, whoever is signed in. A Valur superuser
        // who belongs to a firm therefore runs Valur's workspace on the main app and their
        // firm's on its subdomain (the backend lets superusers administer any workspace:
        // acts_as_workspace_admin), so an invitation or a client assignment lands in the
        // workspace of the host it was made on.
        //
        // One exception, for the workspace only: an advisor whose firm has no live
        // subdomain (self-registered, reserve-don't-serve) has nowhere else to work, so on
        // the main app their workspace falls back to their own membership. The brand never
        // does, and neither does a superuser: they run whichever workspace the host names,
        // so on the main app that is Valur's even when their own firm has no subdomain.
        //
        // This commits both itself rather than handing them back, so no caller can store
        // the wrong thing — every consumer just dispatches and reads a getter. Returns
        // the *host* profile, and only for analytics: hit counting is about real
        // subdomain traffic and must not see the membership fallback.
        //
        // Always go through this rather than calling getAdvisoryPartnerProfile
        // directly: for a reserved firm's advisor that one answers "Valur", which
        // silently swaps their workspace for the house record and aims later writes at
        // Valur's uuid (403).
        async syncAdvisoryPartnerProfile({ commit, dispatch, rootGetters }) {
            const hostProfile = await dispatch('getAdvisoryPartnerProfile');

            // Committed before the membership lookup, which must never reach it.
            if (hostProfile?.name) {
                commit('updateBrandProfile', hostProfile);
            } else if (hostProfile === null) {
                // `null` means the host resolves to no partner (a definitive 404). Clear
                // any stale brand — e.g. one rehydrated from localStorage by
                // vuex-persist. (`undefined` from a network/5xx error is inconclusive, so
                // leave whatever is applied alone.)
                commit('updateBrandProfile', {});
            }

            let profile = hostProfile;
            // `isAdvisor` already means "membership that currently grants access", so a
            // lapsed trial loses the workspace along with everything else — and no
            // client/individual navigation pays for the second request.
            //
            // Deliberately not cached on the committed profile: vuex-persist restores
            // that across reloads, so a "have we resolved this already?" check would
            // pin the advisor to a snapshot and never pick up their own edits.
            const user = rootGetters.user || {};
            const takesFallback = Boolean(user.advisor_workspace?.is_reserved) && !user.is_superuser;
            if (!isTenantPartner(hostProfile) && rootGetters.isAdvisor && takesFallback) {
                profile = mergeWorkspaceProfile(hostProfile, await dispatch('getOwnWorkspaceProfile'));
            }

            if (profile?.name) {
                commit('updateAdvisoryPartnerProfile', profile);
            } else if (profile === null) {
                // Neither the host nor the user resolves to a partner; same tri-state
                // handling as the brand above.
                commit('updateAdvisoryPartnerProfile', {});
            }
            return hostProfile;
        },

        // The caller's own workspace profile, independent of the request host. Used
        // only for an advisor whose firm has no live subdomain, on the main app, so the
        // advisor pages act on their own firm rather than the house record. Never used
        // for branding.
        // `null` = no workspace (404/403, a definitive answer); `undefined` = the
        // request failed inconclusively, so leave the existing workspace alone.
        async getOwnWorkspaceProfile({ dispatch, rootGetters }) {
            try {
                const { data } = await axios.get(`${API_ROOT}/advisory/mine/`, rootGetters.axiosConfig);
                return data;
            } catch (e) {
                const status = e?.response?.status;
                if (status === 404 || status === 403) {
                    return null;
                }
                logApiError(e, dispatch);
                return undefined;
            }
        },
        async updateAdvisoryPartnerProfile({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.patch(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/`,
                    payload,
                    rootGetters.axiosConfig,
                );
                return { ok: true, data };
            } catch (e) {
                // Surface validation kinds (e.g. sharing_unavailable_on_trial)
                // without the generic error toast; real failures still log.
                const kind = apiErrorKind(e);
                if (e.response?.status !== 400) {
                    logApiError(e, dispatch);
                }
                return { ok: false, kind: kind || 'error' };
            }
        },
        async uploadAdvisoryPartnerLogo({ dispatch, rootGetters }, { advisory_partner_id, file }) {
            try {
                const formData = new FormData();
                formData.append('file', file);
                const { data } = await axios.post(
                    `${API_ROOT}/advisory/${advisory_partner_id}/logo/`,
                    formData,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async deleteAdvisoryPartnerLogo({ dispatch, rootGetters }, { advisory_partner_id }) {
            try {
                const { data } = await axios.delete(
                    `${API_ROOT}/advisory/${advisory_partner_id}/logo/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async listAdvisoryPartnerHistory({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/history/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        // Workspace dashboard: firm-wide counters, projects needing attention and
        // upcoming dates in one admin-only read (ENG-2742).
        async getAdvisoryPartnerOverview({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/overview/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return null;
            }
        },
        async listAdvisoryPartnerMembers({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/members/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        // Remove a member. A member who still has assigned clients requires a book
        // decision: `clients: '<member_uuid>'` reassigns the whole book,
        // `clients: 'unassign'` moves it to the Unassigned pool; without one the
        // backend answers 409 book_decision_required (surfaced via `kind`).
        async deleteAdvisoryPartnerMember({ dispatch, rootGetters }, payload) {
            try {
                const config = { ...rootGetters.axiosConfig };
                if (payload.clients) {
                    config.data = { clients: payload.clients };
                }
                await axios.delete(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/members/${payload.advisor_id}/`,
                    config,
                );
                return { ok: true };
            } catch (e) {
                const kind = apiErrorKind(e);
                if (kind === 'book_decision_required') {
                    return { ok: false, kind, clients_count: e.response?.data?.clients_count || 0 };
                }
                if (kind === 'invalid_reassign_target') {
                    return { ok: false, kind };
                }
                logApiError(e, dispatch);
                return { ok: false, kind: 'error' };
            }
        },
        async updateAdvisoryPartnerMember({ dispatch, rootGetters }, payload) {
            try {
                await axios.patch(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/members/${payload.advisor_id}/`,
                    payload,
                    rootGetters.axiosConfig,
                );
                return { ok: true };
            } catch (e) {
                // Surface validation kinds (e.g. sharing_unavailable_on_trial)
                // without the generic error toast; real failures still log.
                const kind = apiErrorKind(e);
                if (e.response?.status !== 400) {
                    logApiError(e, dispatch);
                }
                return { ok: false, kind: kind || 'error' };
            }
        },
        async addAdvisoryPartnerMember({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/advisory/${payload.advisory_partner_id}/members/`,
                    payload,
                    rootGetters.axiosConfig,
                );
                return { ok: true, data };
            } catch (e) {
                const status = e.response?.status;
                const detail = e.response?.data?.detail;
                if (status === 404 && detail === 'user_not_found') {
                    return { ok: false, kind: 'needs_user_info' };
                }
                if (status === 409 && detail === 'already_member') {
                    return { ok: false, kind: 'already_member' };
                }
                // advisor-invite-advisor is off: the address has no account and
                // this surface may not create one.
                if (status === 403 && detail === 'user_creation_disabled') {
                    return { ok: false, kind: 'user_creation_disabled' };
                }
                if (status === 400) {
                    return { ok: false, kind: detail || 'email_invalid' };
                }
                // The whole grant is rolled back when the invite cannot be
                // delivered, so this is "nothing happened", not a partial add.
                if (status === 502 && detail === 'email_send_failed') {
                    return { ok: false, kind: 'email_send_failed' };
                }
                logApiError(e, dispatch);
                return { ok: false, kind: 'error' };
            }
        },
    },
};
